import { CommonModule, isPlatformBrowser, NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  Output,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink, RouterModule } from '@angular/router';
import { DropdownComponent } from '../dropdown/dropdown.component';

import { CDK_DRAG_CONFIG, DragDropModule } from '@angular/cdk/drag-drop';

import { TranslatePipe } from '@ngx-translate/core';
import { LocaleService } from '../../services/locale/locale.service';
import { Subscription } from 'rxjs';
import { StorageService } from 'src/app/Core/_services/storage/storage.service';

export interface ToolboxPosition {
  x: number;
  y: number;
}
export interface MenuItem {
  icon: string;
  text: string;
  route?: string;
  notifications?: number;
  active?: boolean;
}
@Component({
    selector: 'app-toolbox',
        standalone: true,
imports: [
    CommonModule,
    RouterModule,
    MatIconModule,
    RouterLink,
    NgClass,
    DropdownComponent,
    DragDropModule,
    TranslatePipe
],
    templateUrl: './toolbox.component.html',
    styleUrl: './toolbox.component.scss',
    providers: [
        {
            provide: CDK_DRAG_CONFIG,
            useValue: {
                dragStartThreshold: 8,
                pointerDirectionChangeThreshold: 8,
                disableNativeDragInteraction: true,
            },
        },
    ]
})
export class ToolboxComponent {
  @ViewChild('toolboxContainer') toolboxContainer!: ElementRef;

  isToolBoxOpen: boolean = false;
  isDragging: boolean = false;
  isBrowser: any;
  private dirSubscription?: Subscription;

  constructor(
    public router: Router,
    private eRef: ElementRef,
    private renderer: Renderer2,
    private localeService: LocaleService,
    @Inject(PLATFORM_ID) private platformId: any,
    private storageService: StorageService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  @Input() title: string = 'MY ACCOUNT';
  @Input() menuItems: MenuItem[] = [];
  @Input() userName: string = '';
  @Input() profileImg: string = '';
  @Input() colorTheme: 'green' | 'purple' | 'elegant' | 'modern' = 'green';
  @Output() itemSelected = new EventEmitter<MenuItem>();
  @Output() signOut = new EventEmitter<void>();

  isMenuOpen: boolean = true;
  position: ToolboxPosition = { x: 0, y: 0 }; // Position will be set in ngAfterViewInit
  private readonly POSITION_STORAGE_KEY = 'toolbox_position';
  private isRTL: boolean = false;
  ngOnInit(): void {
    // Set at least one item as active by default if none are active
    if (!this.menuItems.some((item) => item.active)) {
      if (this.menuItems.length > 0) {
        this.menuItems[0].active = true;
      }
    }

    // Subscribe to direction changes
    if (this.isBrowser) {
      this.dirSubscription = this.localeService.dir$.subscribe((dir) => {
        if (dir) {
          const newIsRTL = dir === 'rtl';
          // If direction changed, update position
          if (newIsRTL !== this.isRTL) {
            this.isRTL = newIsRTL;
            this.updatePositionForDirection();
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    // Clean up subscription
    if (this.dirSubscription) {
      this.dirSubscription.unsubscribe();
    }
  }
  private keepInViewport(): void {
    if (!this.toolboxContainer) return;

    const el = this.toolboxContainer.nativeElement;
    const rect = el.getBoundingClientRect();
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    // Check if toolbox is outside viewport
    let x = this.position.x;
    let y = this.position.y;

    // Left edge check
    if (rect.left < 0) {
      x = 10;
    }

    // Right edge check - leave room for the menu to open
    if (rect.right > viewportWidth) {
      x = viewportWidth - rect.width - 10;
    }

    // Top edge check
    if (rect.top < 0) {
      y = 10;
    }

    // Bottom edge check
    if (rect.bottom > viewportHeight) {
      y = viewportHeight - rect.height - 10;
    }

    // Update position if changed
    if (x !== this.position.x || y !== this.position.y) {
      this.position = { x, y };
      this.applyPosition();
      this.savePosition();
      this.positionMenu();
    }
  }
  ngAfterViewInit(): void {
    if (this.isBrowser) {
      // Detect RTL from LocaleService
      this.isRTL = this.localeService.dir === 'rtl';

      // Try to load saved position from localStorage
      const savedPosition = this.loadPosition();

      // Validate saved position - if it's too close to top-left corner, reset it
      const isValidPosition =
        savedPosition && savedPosition.x > 15 && savedPosition.y > 50;

      if (isValidPosition) {
        // Use saved position
        this.position = savedPosition;
      } else {
        if (savedPosition) {
          localStorage.removeItem(this.storageService.getPrefixedKey(this.POSITION_STORAGE_KEY));
        }
        // Set initial position based on screen size and direction
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate vertical center position (accounting for element height ~60px)
        const centerY = viewportHeight / 2 - 30;

        // Position based on text direction:
        // RTL (Arabic): Left side
        // LTR (English): Right side
        if (this.isRTL) {
          // Position on left side for RTL
          this.position = { x: 20, y: centerY };
        } else {
          // Position on right side for LTR
          this.position = { x: viewportWidth - 70, y: centerY };
        }
      }

      // Apply position after a short delay to ensure the view is ready
      setTimeout(() => {
        this.applyPosition();
        this.keepInViewport();
      }, 0);
    }
  }
  handleMenuClick(event: Event): void {
    if (event) {
      event.stopPropagation();
    }

    if (this.isDragging) {
      return;
    }

    this.toggleMenu();
  }

  // Save current position to localStorage
  private savePosition(): void {
    localStorage.setItem(
      this.storageService.getPrefixedKey(this.POSITION_STORAGE_KEY),
      JSON.stringify(this.position)
    );
  }

  // Load position from localStorage
  private loadPosition(): ToolboxPosition | null {
    try {
      const saved = localStorage.getItem(this.storageService.getPrefixedKey(this.POSITION_STORAGE_KEY));
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading toolbox position:', e);
    }
    return null;
  }

  // Apply the current position to the element
  private applyPosition(): void {
    // Position is handled by cdkDragFreeDragPosition binding
    // We just need to trigger change detection by updating the position object
    this.position = { ...this.position };
  }

  // Handle drag start
  // Add a mousedown/touchstart tracking variable
  private dragStartTime: number = 0;
  private dragThreshold: number = 100; // milliseconds

  // Constrain the drag position to stay within viewport
  constrainPosition = (point: any, _dragRef: any) => {
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // Get the element dimensions from ViewChild
    if (!this.toolboxContainer?.nativeElement) {
      return point;
    }

    const element = this.toolboxContainer.nativeElement;
    const elementRect = element.getBoundingClientRect();

    // Calculate boundaries with padding
    const padding = 10;
    const minX = padding;
    const minY = padding;
    const maxX = viewport.width - elementRect.width - padding;
    const maxY = viewport.height - elementRect.height - padding;

    // Constrain the position
    return {
      x: Math.max(minX, Math.min(point.x, maxX)),
      y: Math.max(minY, Math.min(point.y, maxY)),
    };
  };

  onDragStart(): void {
    this.dragStartTime = Date.now();
    this.isDragging = true;
    this.forceCloseMenu();

    // Add dragging class for visual feedback
    if (this.toolboxContainer?.nativeElement) {
      this.renderer.addClass(this.toolboxContainer.nativeElement, 'dragging');
    }
  }
  onDragEnd(event: any): void {
    const dragDuration = Date.now() - this.dragStartTime;
    if (this.toolboxContainer?.nativeElement) {
      this.renderer.removeClass(
        this.toolboxContainer.nativeElement,
        'dragging'
      );
    }

    // Get the new position from the drag event
    const newPosition = event.source.getFreeDragPosition();
    this.position = {
      x: newPosition.x,
      y: newPosition.y,
    };

    this.savePosition();
    this.keepInViewport();
    this.positionMenu();

    // Reset dragging state after a short delay to prevent accidental clicks
    setTimeout(() => {
      this.isDragging = false;
    }, 100);
  }
  private forceCloseMenu(): void {
    this.isMenuOpen = false;

    // Remove any open classes from menu element
    if (this.toolboxContainer?.nativeElement) {
      const menuEl =
        this.toolboxContainer.nativeElement.querySelector('.floating-menu');
      if (menuEl) {
        this.renderer.removeClass(menuEl, 'open');
      }
    }
  }
  // Reset position to default based on text direction
  resetPosition(): void {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const centerY = viewportHeight / 2 - 30;

    // Position based on text direction:
    // RTL (Arabic): Left side
    // LTR (English): Right side
    if (this.isRTL) {
      // Position on left side for RTL
      this.position = { x: 20, y: centerY };
    } else {
      // Position on right side for LTR
      this.position = { x: viewportWidth - 70, y: centerY };
    }

    this.applyPosition();
    this.savePosition();
    this.closeMenu();
  }

  // Clear saved position from localStorage (for debugging)
  clearSavedPosition(): void {
    localStorage.removeItem(this.storageService.getPrefixedKey(this.POSITION_STORAGE_KEY));
    this.resetPosition();
  }
  openToolBox() {
    if (!this.isDragging) {
      this.isToolBoxOpen = !this.isToolBoxOpen;
    }
  }

  toggleMenu(): void {
    if (this.isDragging) {
      return;
    }
    this.isMenuOpen = !this.isMenuOpen;

    // If opening menu, ensure it fits on screen
    if (this.isMenuOpen) {
      setTimeout(() => {
        this.positionMenu();
      }, 0);
      setTimeout(() => {
        const menuEl =
          this.toolboxContainer.nativeElement.querySelector('.floating-menu');
        if (menuEl) {
          const menuRect = menuEl.getBoundingClientRect();
          const viewportWidth =
            window.innerWidth || document.documentElement.clientWidth;
          const viewportHeight =
            window.innerHeight || document.documentElement.clientHeight;

          // If menu goes off-screen, adjust its position
          if (menuRect.right > viewportWidth) {
            this.renderer.setStyle(menuEl, 'right', '0');
            this.renderer.setStyle(menuEl, 'left', 'auto');
          }

          if (menuRect.bottom > viewportHeight) {
            this.renderer.setStyle(menuEl, 'bottom', '60px');
            this.renderer.setStyle(menuEl, 'top', 'auto');
          }
        }
      }, 0);
    }
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  selectItem(item: MenuItem): void {
    // Update active status
    this.menuItems.forEach((menuItem) => (menuItem.active = false));
    item.active = true;

    this.itemSelected.emit(item);

    // If it's a mobile view, close the menu after selection
    // if (window.innerWidth < 768) {
    this.closeMenu();
    // }
  }

  handleSignOut(): void {
    this.signOut.emit();
    this.closeMenu();
  }

  // To prevent menu closing when clicking inside
  onMenuClick(event: Event): void {
    event.stopPropagation();
  }
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.closeMenu();
    }
  }
  @HostListener('window:resize', ['$event'])
  onResize() {
    // Direction changes are now handled by dir$ subscription
    if (this.isMenuOpen) {
      this.positionMenu();
    }
    this.keepInViewport();
  }
  // In your toolbox.component.ts

  // Add these as class properties
  private menuPositionClass: string = 'position-right'; // Default position
  private menuOffset = { x: 10, y: 0 }; // Default offset

  /**
   * Calculates and applies the optimal position for the floating menu
   * based on the trigger button's position
   */
  private positionMenu(): void {
    if (!this.toolboxContainer || !this.isMenuOpen) return;

    const containerEl = this.toolboxContainer.nativeElement;
    const triggerEl = containerEl.querySelector('.menu-trigger');
    const menuEl = containerEl.querySelector('.floating-menu');

    if (!triggerEl || !menuEl) return;

    // Get dimensions and positions
    const triggerRect = triggerEl.getBoundingClientRect();
    const menuRect = menuEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Clear previous position classes
    [
      'position-right',
      'position-left',
      'position-top',
      'position-bottom',
    ].forEach((cls) => {
      this.renderer.removeClass(containerEl, cls);
    });

    // Determine best position based on available space
    let bestPosition = 'position-right'; // Default

    // Check right space
    const rightSpace = viewportWidth - triggerRect.right;
    const canShowRight = rightSpace >= menuRect.width + 10;

    // Check left space
    const leftSpace = triggerRect.left;
    const canShowLeft = leftSpace >= menuRect.width + 10;

    // Check bottom space
    const bottomSpace = viewportHeight - triggerRect.bottom;
    const canShowBottom = bottomSpace >= menuRect.height + 10;

    // Check top space
    const topSpace = triggerRect.top;
    const canShowTop = topSpace >= menuRect.height + 10;

    // Determine best position based on available space
    if (canShowRight) {
      bestPosition = 'position-right';
      this.menuOffset = { x: 10, y: 0 };
    } else if (canShowLeft) {
      bestPosition = 'position-left';
      this.menuOffset = { x: -10, y: 0 };
    } else if (canShowBottom) {
      bestPosition = 'position-bottom';
      this.menuOffset = { x: 0, y: 10 };
    } else if (canShowTop) {
      bestPosition = 'position-top';
      this.menuOffset = { x: 0, y: -10 };
    } else {
      // Fallback: position where most space is available
      const spaces = [
        { pos: 'position-right', space: rightSpace },
        { pos: 'position-left', space: leftSpace },
        { pos: 'position-bottom', space: bottomSpace },
        { pos: 'position-top', space: topSpace },
      ];

      // Sort by available space (descending)
      spaces.sort((a, b) => b.space - a.space);
      bestPosition = spaces[0].pos;
    }

    // Apply position class
    this.renderer.addClass(containerEl, bestPosition);
    this.menuPositionClass = bestPosition;

    // Apply specific position adjustments
    this.applyMenuPosition(menuEl, triggerRect, menuRect);
  }

  /**
   * Applies specific position adjustments to the menu element
   */
  private applyMenuPosition(
    menuEl: HTMLElement,
    triggerRect: DOMRect,
    menuRect: DOMRect
  ): void {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Reset any inline positioning
    this.renderer.removeStyle(menuEl, 'top');
    this.renderer.removeStyle(menuEl, 'right');
    this.renderer.removeStyle(menuEl, 'bottom');
    this.renderer.removeStyle(menuEl, 'left');

    // Apply positioning based on position class
    switch (this.menuPositionClass) {
      case 'position-right':
        // Position to the right of the trigger
        this.renderer.setStyle(
          menuEl,
          'left',
          `${triggerRect.width + this.menuOffset.x}px`
        );
        this.renderer.setStyle(menuEl, 'top', '0');

        // Check if menu goes below viewport
        if (triggerRect.top + menuRect.height > viewportHeight) {
          const bottomSpace = viewportHeight - triggerRect.top;
          this.renderer.setStyle(menuEl, 'max-height', `${bottomSpace - 20}px`);
          this.renderer.setStyle(menuEl, 'overflow-y', 'auto');
        }
        break;

      case 'position-left':
        // Position to the left of the trigger
        this.renderer.setStyle(
          menuEl,
          'right',
          `${triggerRect.width + this.menuOffset.x}px`
        );
        this.renderer.setStyle(menuEl, 'top', '0');

        // Check if menu goes below viewport
        if (triggerRect.top + menuRect.height > viewportHeight) {
          const bottomSpace = viewportHeight - triggerRect.top;
          this.renderer.setStyle(menuEl, 'max-height', `${bottomSpace - 20}px`);
          this.renderer.setStyle(menuEl, 'overflow-y', 'auto');
        }
        break;

      case 'position-bottom':
        // Position below the trigger
        this.renderer.setStyle(
          menuEl,
          'top',
          `${triggerRect.height + this.menuOffset.y}px`
        );

        // Center horizontally if enough space
        if (
          triggerRect.left + menuRect.width / 2 < viewportWidth &&
          triggerRect.right - menuRect.width / 2 > 0
        ) {
          // Center align with trigger
          this.renderer.setStyle(
            menuEl,
            'left',
            `${(triggerRect.width - menuRect.width) / 2}px`
          );
        } else if (triggerRect.left + menuRect.width > viewportWidth) {
          // Align right edge with viewport
          this.renderer.setStyle(menuEl, 'right', '10px');
        } else {
          // Align left edge with viewport
          this.renderer.setStyle(menuEl, 'left', '10px');
        }
        break;

      case 'position-top':
        // Position above the trigger
        this.renderer.setStyle(
          menuEl,
          'bottom',
          `${triggerRect.height + Math.abs(this.menuOffset.y)}px`
        );

        // Center horizontally if enough space
        if (
          triggerRect.left + menuRect.width / 2 < viewportWidth &&
          triggerRect.right - menuRect.width / 2 > 0
        ) {
          // Center align with trigger
          this.renderer.setStyle(
            menuEl,
            'left',
            `${(triggerRect.width - menuRect.width) / 2}px`
          );
        } else if (triggerRect.left + menuRect.width > viewportWidth) {
          // Align right edge with viewport
          this.renderer.setStyle(menuEl, 'right', '10px');
        } else {
          // Align left edge with viewport
          this.renderer.setStyle(menuEl, 'left', '10px');
        }
        break;
    }
  }
  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange() {
    // Give the browser time to update dimensions
    setTimeout(() => {
      // Direction changes are now handled by dir$ subscription
      this.keepInViewport();

      // If menu is open, ensure it's properly positioned
      if (this.isMenuOpen) {
        this.positionMenu();
        this.closeMenu();
        setTimeout(() => this.toggleMenu(), 100);
      }
    }, 200);
  }

  // Update position when direction changes
  private updatePositionForDirection(): void {
    if (!this.isBrowser) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const centerY = viewportHeight / 2 - 30;

    // Position based on text direction:
    // RTL (Arabic): Left side
    // LTR (English): Right side
    if (this.isRTL) {
      // Move to left side for RTL
      this.position = { x: 20, y: centerY };
    } else {
      // Move to right side for LTR
      this.position = { x: viewportWidth - 70, y: centerY };
    }

    this.applyPosition();
    this.savePosition();
  }
}
